/* globals RCM, Vimeo */
/* eslint-disable @wordpress/no-global-active-element */

/**
 * External dependencies
 */
import CliboardJS from 'clipboard';

/**
 * Internal dependencies
 */
import delegateEventListener from './theme/delegate-event';
import determineTarget from './theme/determine-target';
import toggleElement from './theme/toggle-element';
import setupStickyObserver from './theme/sticky-observer';
import setupTabs from './theme/tabs';
import setupAccordions from './theme/accordions';
import setupModals from './theme/modals';
import setupShareButtons from './theme/share-button';

function domReady( callback ) {
	if ( document.readyState === 'complete' ) {
		callback();
	} else {
		document.addEventListener( 'DOMContentLoaded', callback );
	}
}

function debounce( callback, timeout ) {
	let timer;
	return ( ...args ) => {
		clearTimeout( timer );
		timer = setTimeout( () => callback( ...args ), timeout );
	};
}

function addClassToAll( selector, className ) {
	document.querySelectorAll( selector ).forEach( element => element.classList.add( ...className.split( ' ' ) ) );
}

domReady( () => {
	// =========================
	// ! Flag Interaction Method
	// =========================

	function flagMouseUse() {
		document.body.classList.add( 'using-mouse' );
		document.body.classList.remove( 'using-keyboard' );
	}
	document.addEventListener( 'mousemove', flagMouseUse );
	document.addEventListener( 'touchstart', flagMouseUse );
	document.addEventListener( 'keydown', event => {
		if ( event.key === 'Tab' ) {
			document.body.classList.remove( 'using-mouse' );
			document.body.classList.add( 'using-keyboard' );
		}
	} );

	// =========================
	// ! External Link Mandate
	// =========================

	document.querySelectorAll( 'a[href^="http"]' ).forEach( anchor => {
		if ( ! anchor.target && ! anchor.href.startsWith( window.location.origin ) ) {
			anchor.setAttribute( 'target', '_blank' );
			anchor.setAttribute( 'rel', 'noreferrer noopener' );
		}
	} );

	// =========================
	// ! Basic Toggles
	// =========================

	delegateEventListener( 'click', '.js-toggle', ( event, trigger ) => {
		const target = determineTarget( trigger );
		if ( target ) {
			event.preventDefault();
			const isOpen = toggleElement( target, null, 'focus' );

			// For global styling that might be needed
			document.body.classList.toggle( `show-${ target.id }`, isOpen );
		}
	} );

	delegateEventListener( 'click', '.js-close', ( event, trigger ) => {
		event.preventDefault();
		toggleElement( trigger.closest( '[aria-hidden="false"]' ), false );
	} );

	// =========================
	// ! Animation Toggle
	// =========================

	let ANIMATIONS_PAUSED = false;
	delegateEventListener( 'click', '.js-animation-toggle', () => {
		ANIMATIONS_PAUSED = ! ANIMATIONS_PAUSED;
		document.body.classList.toggle( 'animations-paused', ANIMATIONS_PAUSED );

		document.querySelectorAll( 'video' ).forEach( video => {
			if ( ANIMATIONS_PAUSED ) {
				video.pause();
				video.dataset.wasPaused = 1;
			} else if ( video.dataset.wasPaused ) {
				video.play();
			}
		} );

		const event = new CustomEvent( `animations-${ ANIMATIONS_PAUSED ? 'paused' : 'playing' }`, {
			bubbles: true,
		} );
		document.dispatchEvent( event );
	} );

	// =========================
	// ! Intersection Observers
	// =========================

	if ( typeof IntersectionObserver !== 'undefined' ) {
		setupStickyObserver();
	}

	// =========================
	// ! Modals
	// =========================

	// Add classes to modals automatically
	addClassToAll( '.rcm-modal', 'js-modal' );
	addClassToAll( '.rcm-modal__close', 'js-modal-close' );
	addClassToAll( '.rcm-modal__frame', 'js-modal-backdrop' );

	setupModals( RCM.endpoints || [] );

	document.querySelectorAll( '.rcm-modal' ).forEach( modal => {
		const video = modal.querySelector( 'video' );
		if ( video ) {
			modal.addEventListener( 'toggle-on', () => {
				video.play();
			} );
			modal.addEventListener( 'toggle-off', () => {
				video.pause();
			} );
		}

		const iframe = modal.querySelector( 'iframe' );
		if ( iframe && typeof Vimeo !== 'undefined' ) {
			const player = new Vimeo.Player( iframe );

			modal.addEventListener( 'toggle-on', () => {
				player.play();
			} );
			modal.addEventListener( 'toggle-off', () => {
				player.pause();
			} );
		}

		document.body.append( modal );
	} );

	// =========================
	// ! Tab Set
	// =========================

	// Add classes to tab/tabset blocks automatically
	addClassToAll( '.rcm-tab-set', 'js-tab-set js-accordion-group' );
	addClassToAll( '.rcm-tab-set__toggle', 'js-tab' );
	addClassToAll( '.rcm-tab-panel', 'js-tab-panel' );
	addClassToAll( '.rcm-tab-panel__toggle', 'js-accordion-toggle js-tablet-toggle' );
	addClassToAll( '.rcm-tab-panel__content', 'js-accordion-content js-tablet-accordion' );

	// Mark first tab as auto-open accordion
	document.querySelectorAll( '.rcm-tab-set' ).forEach( tabSet => {
		tabSet.querySelector( '.rcm-tab-panel__toggle' )?.classList.add( 'auto-open' );
	} );

	setupTabs();

	// =========================
	// ! Accordion
	// =========================

	// Add classes to accordion blocks automatically
	addClassToAll( '.rcm-accordion__toggle', 'js-accordion-toggle' );
	addClassToAll( '.rcm-accordion__content', 'js-accordion-content' );

	// Auto open conditions: contains search highlight, or is target
	addClassToAll( '.rcm-accordion:has(mark) .rcm-accordion__toggle', 'auto-open' );
	if ( document.location.hash.length > 1 ) {
		document.querySelector( `.rcm-accordion${ document.location.hash } .rcm-accordion__toggle` )?.classList.add( 'auto-open' );
	}

	setupAccordions( RCM.endpoints || [] );

	// =========================
	// ! Scroller Buttons
	// =========================

	function makeScrollerButton( type ) {
		const button = document.createElement( 'div' );
		button.className = `rcm-scroller__button ${ type }`;
		button.setAttribute( 'aria-hidden', true );
		return button;
	}

	const scrollerGaps = new WeakMap();
	const scrollerGapObserver = new ResizeObserver( entries => {
		entries.forEach( entry => {
			const gap = parseInt( getComputedStyle( entry.target ).gap.replace( 'px', '' ) );
			scrollerGaps.set( entry.target, gap );
		} );
	} );

	document.querySelectorAll( '.rcm-scroller' ).forEach( scroller => {
		const track = scroller.querySelector( '.rcm-scroller__track' );

		const nav = document.createElement( 'div' );
		nav.classList.add( 'rcm-scroller__nav' );
		scroller.append( nav );

		const nextButton = makeScrollerButton( 'next' );
		const prevButton = makeScrollerButton( 'prev' );
		const buttons = [ nextButton, prevButton ];

		nav.append( prevButton, nextButton );

		prevButton.hidden = true;
		track.addEventListener( 'scroll', debounce( () => {
			nextButton.hidden = track.scrollLeft + track.offsetWidth >= track.scrollWidth;
			prevButton.hidden = track.scrollLeft === 0;
		}, 100 ) );

		scrollerGapObserver.observe( track );

		let sampleItem = track.firstElementChild;
		if ( sampleItem.classList.contains( 'rcm-box-list' ) ) {
			sampleItem = sampleItem.firstElementChild;
		}

		buttons.forEach( button => {
			button.addEventListener( 'click', () => {
				if ( button.hidden ) {
					return;
				}

				const gap = scrollerGaps.get( track ) ?? 0;
				if ( button.classList.contains( 'next' ) ) {
					track.scrollBy( {
						left: sampleItem.offsetWidth + gap,
					} );
				} else {
					track.scrollBy( {
						left: -1 * ( sampleItem.offsetWidth + gap ),
					} );
				}
			} );
		} );
	} );

	document.addEventListener( 'keyup', event => {
		if ( event.key === 'Tab' ) {
			const scroller = document.activeElement?.closest( '.rcm-scroller__track' );
			if ( scroller ) {
				document.activeElement.scrollIntoView( {
					block: 'nearest',
					inline: 'nearest',
				} );
			}
		}
	} );

	// =========================
	// ! Slideshow Module
	// =========================

	document.querySelectorAll( '.rcm-slideshow' ).forEach( slideshow => {
		const slidesWrap = slideshow.querySelector( '.rcm-slideshow__slides' );
		const slides = slideshow.querySelectorAll( '.rcm-slideshow-slide' );

		const progress = document.createElement( 'div' );
		progress.className = 'rcm-slideshow__progress';
		slidesWrap.append( progress );

		const dots = [];
		slides.forEach( ( slide, index ) => {
			slide.setAttribute( 'aria-hidden', index > 0 );
			const dot = document.createElement( 'div' );
			dot.className = 'rcm-slideshow__progress__dot';
			progress.append( dot );
			dots.push( dot );
		} );

		function goToSlide( newIndex ) {
			slides.forEach( ( slide, index ) => slide.setAttribute( 'aria-hidden', index !== newIndex ) );
			dots.forEach( ( dot, index ) => dot.classList.toggle( 'active', index === newIndex ) );
		}

		slideshow.style.setProperty( '--slide-count', slides.length );

		window.addEventListener( 'scroll', function() {
			const position = window.scrollY - slideshow.offsetTop;

			const index = Math.max(
				0,
				Math.min(
					Math.round( ( position / slideshow.offsetHeight ) * slides.length ),
					slides.length - 1,
				),
			);

			goToSlide( index );
		}, { passive: true } );
	} );

	// =========================
	// ! Hidden Target Handling
	// =========================

	function handleHiddenTarget( event ) {
		// Get the target element
		const target = document.getElementById( window.location.hash.substring( 1 ) );
		if ( ! target ) {
			return;
		}

		// See if it's within a hidden container
		const container = target.closest( '[id][aria-hidden="true"]' );
		if ( ! container ) {
			return;
		}

		// Find the control for the container
		const control = document.querySelector( `[aria-controls="${ container.id }"], [data-target="${ container.id }"]` );
		if ( ! control ) {
			return;
		}

		// Prevent automatic scolling
		if ( event ) {
			event.preventDefault();
		}

		// Open the container and focus
		control.click();
		target.scrollIntoView();
	}

	window.addEventListener( 'hashchange', handleHiddenTarget );
	handleHiddenTarget();

	// =========================
	// ! Bookmark/Share Links
	// =========================

	setupShareButtons();

	delegateEventListener( 'click', 'a.js-clipboard', event => event.preventDefault() );

	const hrefClipboard = new CliboardJS( 'a.js-clipboard', {
		text( trigger ) {
			return trigger.getAttribute( 'href' );
		},
	} );

	const clipboardTooltipTimeouts = new WeakMap();
	hrefClipboard.on( 'success', event => {
		clearTimeout( clipboardTooltipTimeouts.get( event.trigger ) );
		event.trigger.classList.add( 'js-copied' );
		event.trigger.setAttribute( 'aria-label', 'Link copied!' );
		const timeout = setTimeout( () => event.trigger.classList.remove( 'js-copied' ), 5000 );
		clipboardTooltipTimeouts.set( event.trigger, timeout );
	} );

	// =========================
	// ! Decor Setup
	// =========================

	document.querySelectorAll( '[data-decor]' ).forEach( element => {
		const type = element.dataset.decor;
		const count = parseInt( type );

		if ( ! isNaN( count ) ) {
			const layer = document.createElement( 'div' );
			layer.className = 'rcm-decor-layer';
			element.prepend( layer );

			for ( let i = 1; i <= count; i++ ) {
				const decor = document.createElement( 'div' );
				decor.className = `rcm-decor rcm-decor--${ i }`;
				layer.append( decor );
			}
			return;
		}

		element.classList.add( `with-decor-${ type }` );

		const foreground = document.createElement( 'div' );
		foreground.className = 'rcm-decor-layer rcm-decor-layer--foreground';

		const background = document.createElement( 'div' );
		background.className = 'rcm-decor-layer rcm-decor-layer--background';

		element.prepend( foreground );
		element.prepend( background );
	} );

	// =========================
	// ! Squircles
	// =========================

	function calculateSquircle( width, height, ratio = 0 ) {
		width = Math.round( width );
		height = Math.round( height );
		const xMid = width / 2;
		const yMid = height / 2;
		const inset = Math.min( yMid, xMid ) * ratio;

		return `
			M 0,${ yMid }
			C 0,${ inset } ${ inset },0 ${ xMid },0
			C ${ width - inset },0 ${ width },${ inset } ${ width },${ yMid }
			C ${ width },${ height - inset } ${ width - inset },${ height } ${ xMid },${ height }
			C ${ inset },${ height } 0,${ height - inset } 0,${ yMid }
			Z
		`;
	}

	const squircles = new Map();
	function registerSquircle( width, height ) {
		if ( ! width || ! height ) {
			return '1'; // default to square
		}

		const aspect = Math.round( ( width / height ) * 10 ) / 10;
		const key = aspect.toFixed( 1 ).replace( '.', '-' );

		if ( ! squircles.get( key )?.parentElement ) {
			width = 100;
			height = width / aspect;

			const markup = `
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${ width } ${ height }" preserveAspectRatio="none">
					<path d="${ calculateSquircle( width, height, 0.05 ) }" />
				</svg>
			`;

			const css = `
				:root {
					--squircle--${ key }: url('data:image/svg+xml;charset=utf8,${ encodeURI( markup ) }');
				}
			`;

			const style = document.createElement( 'style' );
			style.appendChild( document.createTextNode( css ) );
			document.head.append( style );

			squircles.set( key, style );
		}

		return key;
	}

	const squircleObserver = new ResizeObserver( entries => {
		for ( const entry of entries ) {
			const box = entry.borderBoxSize[ 0 ];
			const width = Math.round( box.inlineSize );
			const height = Math.round( box.blockSize );

			entry.target.style.setProperty( '--squircle', `var(--squircle--${ registerSquircle( width, height ) })` );
		}
	} );

	// Setup custom squircle support on established elements
	addClassToAll( '.wp-block-image.is-style-squircle img', 'squircle' );
	addClassToAll( '.wp-block-media-text__media img', 'squircle' );
	addClassToAll( '.wp-block-group.is-style-wrapper', 'squircle' );
	addClassToAll( '.rcm-banner, .rcm-box, .rcm-video', 'squircle' );
	addClassToAll( '.rcm-splash-banner__backdrop', 'squircle' );
	addClassToAll( '.pathway-lockup :is(.wp-block-heading, .wp-block-image)', 'squircle' );

	document.querySelectorAll( '.squircle' ).forEach( element => {
		squircleObserver.observe( element );
	} );

	// =========================
	// ! Box List Counter Fix (Safari)
	// =========================

	document.querySelectorAll( 'ol.rcm-box-list > li' ).forEach( ( item, index ) => item.style.setProperty( '--count', `"${ index + 1 }"` ) );
} );
